/**
 * mapCameraKey.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * بصمة حالة الكاميرا — لتخطّي إعادة وضع العلامات حين لا تتحرّك الخريطة.
 *
 * ── المشكلة التي يعالجها ──────────────────────────────────────────────────────
 *
 * الطبقات ذات العلامات في الـDOM (الحرائق، البرق، الأعاصير) تستمع إلى حدث
 * `render` من MapLibre وتُعيد إسقاط كل علامة عنده. والاسم يُضلِّل: `render` لا يعني
 * «تحرّكت الخريطة» بل «رُسم إطار». وطبقة الجسيمات طبقةٌ مخصّصة تستدعي
 * `triggerRepaint()` باستمرار كي تتحرّك الجسيمات — فيُطلَق `render` **ستّين مرّة في
 * الثانية بلا انقطاع، والخريطة ساكنة تماماً**.
 *
 * النتيجة على خريطة ساكنة: لكل علامة `project()` وكتابتان على النمط، ستّين مرّة في
 * الثانية. مع مئات البؤر الحرارية يصير ذلك عشرات آلاف العمليات في الثانية على
 * الخيط الرئيسي — بلا أي تغيّر في الصورة، إذ إن مواضع العلامات دالّةٌ في الكاميرا
 * وحدها ولم تتغيّر الكاميرا.
 *
 * وهذا يفسّر ما رُصد بالضبط: «تشغيل الحرائق يُبطئ الطبقات **والجسيمات**». الجسيمات
 * ضحيّة لا سبب — هي تطلب الرسم، والطبقات الأخرى تعلّق نفسها على طلبها.
 *
 * ── الحلّ ─────────────────────────────────────────────────────────────────────
 *
 * بصمة رقمية من مركز الخريطة وتكبيرها ودورانها وميلها وحجمها. تساوي البصمةَ
 * السابقة ⇒ لا شيء يتغيّر في أي موضع ⇒ اخرج فوراً. فتصير كلفة الخريطة الساكنة
 * مقارنةَ عددٍ واحد بدل مسح كل العلامات.
 *
 * لا أثر بصري إطلاقاً: ما يُتخطّى حسابٌ نتيجته مطابقة لما هو مرسوم أصلاً.
 */

import type { Map as MapLibreMap } from 'maplibre-gl';

/**
 * كائن يكفي لحساب البصمة — نقبل أي شيء يوفّر هذه التوابع كي تعمل الدالّة مع
 * `MapRef` من react-map-gl ومع خريطة MapLibre الخام سواءً بسواء.
 */
type CameraSource = Pick<MapLibreMap, 'getCenter' | 'getZoom' | 'getBearing' | 'getPitch'> & {
    getCanvas?: () => HTMLCanvasElement;
};

/**
 * بصمة الكاميرا الحالية. أي تغيّر في المركز أو التكبير أو الدوران أو الميل أو حجم
 * اللوحة يُنتج بصمة مختلفة.
 *
 * التقريب مقصود: خمس منازل عشرية لخط الطول/العرض (~1 متر عند خط الاستواء) وأربع
 * للتكبير. أدقّ من ذلك يجعل الاهتزاز العددي الطفيف في MapLibre يُبطل الحارس بلا
 * حركة فعلية، وأخشن منه قد يُهمل إزاحة يراها المستخدم.
 */
export function cameraKey(map: CameraSource): string {
    const c = map.getCenter();
    const canvas = map.getCanvas?.();
    return `${c.lng.toFixed(5)},${c.lat.toFixed(5)},${map.getZoom().toFixed(4)},`
        + `${map.getBearing().toFixed(2)},${map.getPitch().toFixed(2)},`
        + `${canvas ? canvas.width : 0}x${canvas ? canvas.height : 0}`;
}
